import { Injectable } from '@angular/core';
import { CriticalFlowService } from 'src/app/core/services/critical-flow.service';

/** Why applying an update was held back. Asserted directly in specs. */
export type UnsafeReason = 'offline' | 'critical-flow';

/**
 * Decides whether the app may be replaced with a newer build during a navigation
 * the user just triggered.
 *
 * Deliberately narrow. A half-filled form or an open modal is NOT a reason to
 * hold back: the user is leaving that screen either way, so the in-app navigation
 * would have discarded it too. Only two things genuinely must not be interrupted.
 *
 * There is no user-activity/idle tracking here. Inferring "is the user busy?"
 * from mousemove proved unreliable — the window reset constantly and the update
 * could fail to land at all. A navigation needs no such guess.
 */
@Injectable({ providedIn: 'root' })
export class ReloadSafetyService {

  constructor(private criticalFlowService: CriticalFlowService) {}

  navigationBlockingReason(): UnsafeReason | null {
    // Reloading with no network lands the user on a dead page: the service
    // worker may not hold the new assets yet and nothing can fetch them.
    if (this.isOffline()) { return 'offline'; }
    // Money in flight — Fireblocks signing, a submitted borrow/loan tx.
    if (this.criticalFlowService.isActive) { return 'critical-flow'; }
    return null;
  }

  /** `onLine === false` is a reliable negative; `true` only means "has a link". */
  private isOffline(): boolean {
    return typeof navigator !== 'undefined' && navigator.onLine === false;
  }
}
